/**
 * webhookId values from n8n-inventory.json (L4L Inventory API).
 * Every operation uses the same URL shape as create/list, which already works:
 *   {VITE_N8N_ORIGIN}/{webhookId}/l4l/inventories/...
 *
 * Path-only URLs (`/webhook/l4l/inventories/...`) 404 on Render without CORS
 * headers, which the browser surfaces as "Failed to fetch" — so item/photo
 * saves never ran after the inspection was created.
 */
export const N8N_INVENTORY_WEBHOOK_IDS = {
  listInventoryInspections: "8b00ddff-6acf-47f8-b42d-a546d5ebe8d4",
  createInventoryInspection: "9dcb8d2f-0911-45b3-8ff0-e4d0050976f5",
  getInventoryInspection: "f9662df3-f9ac-4cbf-9b0e-f208d1f29565",
  updateInventoryInspection: "b5df325e-1a1e-47a9-bb6d-b925ac89ffb2",
  createInventoryInspectionSection: "55609bdd-0c87-4a30-b8ee-69dd1554d5d8",
  updateInventoryInspectionSection: "509e1416-6ca3-421a-9f14-2377bda98041",
  deleteInventoryInspectionSection: "6a32aee3-894a-45c3-8339-075ab8731b51",
  createInventoryInspectionItem: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  updateInventoryInspectionItem: "b2c3d4e5-f6a7-8901-bcde-f12345678901",
  deleteInventoryInspectionItem: "c3d4e5f6-a7b8-9012-cdef-123456789012",
  generateInventoryInspectionPdf: "d4e5f6a7-b8c9-0123-def0-234567890123",
  getUploadUrls: "e5f6a7b8-c9d0-1234-ef01-345678901234",
  getInventoryCatalog: "9f4721a7-686c-4eaf-a4a6-8a4b3141ed87",
} as const;

export type N8nInventoryOperation = keyof typeof N8N_INVENTORY_WEBHOOK_IDS;

const INVENTORIES_PATH_PREFIX = "l4l/inventories";

export const getN8nOrigin = (): string | undefined => {
  const origin = import.meta.env.VITE_N8N_ORIGIN;
  if (!origin || typeof origin !== "string") return undefined;
  return origin.replace(/\/$/, "");
};

export const usesN8nWebhookIds = (): boolean => Boolean(getN8nOrigin());

const toResourcePath = (resourcePath: string): string =>
  resourcePath.startsWith("/") ? resourcePath : `/${resourcePath}`;

/**
 * Candidate URLs for one operation. webhookId-prefixed first (same as create),
 * path-only second so a re-imported workflow still matches by path.
 */
export const buildN8nInventoryUrls = (
  operation: N8nInventoryOperation,
  resourcePath: string
): string[] => {
  const origin = getN8nOrigin();
  if (!origin) {
    throw new Error("VITE_N8N_ORIGIN is not set");
  }

  const path = toResourcePath(resourcePath);
  const webhookId = N8N_INVENTORY_WEBHOOK_IDS[operation];
  const withWebhookId = `${origin}/${webhookId}/${INVENTORIES_PATH_PREFIX}${path}`;
  const pathOnly = `${origin}/${INVENTORIES_PATH_PREFIX}${path}`;

  return withWebhookId === pathOnly ? [withWebhookId] : [withWebhookId, pathOnly];
};

/**
 * @param resourcePath Path after l4l/inventories, e.g. /properties/{id}/inventory-inspections
 */
export const buildN8nInventoryUrl = (
  operation: N8nInventoryOperation,
  resourcePath: string
): string => {
  return buildN8nInventoryUrls(operation, resourcePath)[0];
};
